import { useState } from 'react';
import { isMobile } from 'react-device-detect';
import {
  ActionIcon,
  Anchor,
  Avatar,
  Button,
  Checkbox,
  Divider,
  Group,
  Stack,
  Table,
  Text,
  Tooltip,
} from '@mantine/core';
import { BiCheckDouble } from 'react-icons/bi';
import { TodoType } from '@th-types/inbox-reviews.type';
import type { TodoItem } from '@th-types/inbox-reviews.type';
import type { TableSelectionProps } from '@hooks/useTableSelection';
import { formatLocalDate } from '@utils/DateUtils';
import DATE_FORMATS from '@constants/dateFormat';
import { TABLE_HEADER_STYLE } from '@components/InboxReviews/tableStyles';
import TodoListItem, {
  TODO_TYPE_LABELS,
  renderTodoMessageWithCompanyLink,
} from './TodoListItem';

function renderEventCell({
  todo,
  eventHref,
  eventOnClick,
}: {
  todo: TodoItem;
  eventHref: string | undefined;
  eventOnClick: (() => void) | undefined;
}): React.ReactNode {
  if (!todo.eventName) return '-';
  if (eventHref) {
    return (
      <Anchor
        href={eventHref}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        c="inherit"
        underline="always"
        fw="inherit"
        size="sm"
      >
        {todo.eventName}
      </Anchor>
    );
  }
  if (eventOnClick) {
    return (
      <Anchor
        component="button"
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          eventOnClick();
        }}
        c="inherit"
        underline="always"
        fw="inherit"
        size="sm"
      >
        {todo.eventName}
      </Anchor>
    );
  }
  return todo.eventName;
}

const TODO_ACTION_LABELS: Record<TodoType, string> = {
  [TodoType.COMPANY_SHARED_JOB]: 'View Job',
  [TodoType.RECOMMENDED_JOB]: 'View Job',
  [TodoType.COMPANY_OFFERS_JOB]: 'View Offer',
  [TodoType.COMPANY_OFFERS_SHIFTS]: 'View Shifts Offer',
  [TodoType.ENTER_HOURS]: 'Enter Hours',
  [TodoType.ENTER_RATING]: 'Rate',
  [TodoType.ENTER_RATING_PAYMENT]: 'Rate',
};

interface TodoNotificationsTableProps {
  todos: TodoItem[];
  onOpenTodo?: (todo: TodoItem) => void;
  selectionProps?: TableSelectionProps<TodoItem>;
  getCompanyHref?: (todo: TodoItem) => string | undefined;
  getEventHref?: (todo: TodoItem) => string | undefined;
  /**
   * Returns a click handler for the event name when no external href applies
   * (e.g., a recommendation that opens a local modal). When both href and
   * click are available for a todo, href wins.
   */
  getEventOnClick?: (todo: TodoItem) => (() => void) | undefined;
  onDismiss?: (todo: TodoItem) => void;
  dismissingId?: number | null;
}

export default function TodoNotificationsTable({
  todos,
  onOpenTodo,
  selectionProps,
  getCompanyHref,
  getEventHref,
  getEventOnClick,
  onDismiss,
  dismissingId,
}: TodoNotificationsTableProps) {
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  if (!todos.length) {
    return (
      <Text c="dimmed" ta="center" py="md" size="sm">
        No pending to-dos.
      </Text>
    );
  }

  if (isMobile) {
    return (
      <Stack gap={0} w="100%">
        {todos.map((todo, index) => (
          <div
            key={`${todo.jobRecommendationId ?? todo.jobAssignmentId}-${todo.type}-${todo.shiftId}`}
          >
            {index > 0 && <Divider />}
            <TodoListItem
              todo={todo}
              onOpen={onOpenTodo}
              companyHref={getCompanyHref?.(todo)}
              onDismiss={onDismiss}
              dismissingId={dismissingId}
            />
          </div>
        ))}
      </Stack>
    );
  }

  return (
    <Table highlightOnHover w="100%" style={{ tableLayout: 'fixed' }}>
      <Table.Thead>
        <Table.Tr>
          {selectionProps && (
            <Table.Th style={TABLE_HEADER_STYLE} w={40}>
              <Checkbox
                size="sm"
                checked={selectionProps.allSelected}
                indeterminate={selectionProps.someSelected}
                onChange={selectionProps.onToggleAll}
                aria-label="Select all todos"
              />
            </Table.Th>
          )}
          <Table.Th style={TABLE_HEADER_STYLE} w={48} />
          <Table.Th style={TABLE_HEADER_STYLE}>Notification</Table.Th>
          <Table.Th style={TABLE_HEADER_STYLE} w={300}>
            Event
          </Table.Th>
          <Table.Th style={TABLE_HEADER_STYLE} w={150}>
            Type
          </Table.Th>
          <Table.Th
            style={{ ...TABLE_HEADER_STYLE, whiteSpace: 'nowrap' }}
            ta="right"
            w={150}
          >
            Date
          </Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {todos.map((todo) => {
          const rowKey = `${todo.jobRecommendationId ?? todo.jobAssignmentId}-${todo.type}-${todo.shiftId}`;
          const companyHref = getCompanyHref?.(todo);
          const eventHref = getEventHref?.(todo);
          const eventOnClick = getEventOnClick?.(todo);

          return (
            <Table.Tr
              key={rowKey}
              style={{ cursor: 'pointer' }}
              fw={700}
              onClick={() => onOpenTodo?.(todo)}
              onMouseEnter={() => setHoveredId(rowKey)}
              onMouseLeave={() => setHoveredId(null)}
            >
              {selectionProps && (
                <Table.Td w={40}>
                  <Checkbox
                    size="sm"
                    checked={selectionProps.selectedIds.has(
                      selectionProps.getId(todo)
                    )}
                    onChange={() => selectionProps.onToggle(todo)}
                    onClick={(e) => e.stopPropagation()}
                    aria-label={`Select todo: ${todo.todoMessage}`}
                  />
                </Table.Td>
              )}
              <Table.Td w={48}>
                {todo.companyLogoUrl && (
                  <Avatar src={todo.companyLogoUrl} size="md" radius="sm" />
                )}
              </Table.Td>
              <Table.Td fz={14}>
                <Text size="sm" fw="inherit" truncate title={todo.todoMessage}>
                  {renderTodoMessageWithCompanyLink(
                    todo.todoMessage,
                    todo.companyName,
                    companyHref
                  )}
                </Text>
              </Table.Td>
              <Table.Td fz={14}>
                <Text
                  size="sm"
                  fw="inherit"
                  truncate
                  title={todo.eventName ?? undefined}
                >
                  {renderEventCell({ todo, eventHref, eventOnClick })}
                </Text>
              </Table.Td>
              <Table.Td fz={14}>
                <Text size="sm">
                  {TODO_TYPE_LABELS[todo.type] ?? todo.type}
                </Text>
              </Table.Td>
              <Table.Td ta="right" fz={14} style={{ whiteSpace: 'nowrap' }}>
                <Group gap="xs" justify="flex-end" wrap="nowrap" mih={40}>
                  {hoveredId === rowKey ? (
                    <>
                      <Button
                        size="compact-sm"
                        fw={700}
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpenTodo?.(todo);
                        }}
                      >
                        {TODO_ACTION_LABELS[todo.type] ?? 'Open'}
                      </Button>
                      {onDismiss && todo.jobRecommendationId != null && (
                        <Tooltip label="Dismiss this job">
                          <ActionIcon
                            variant="transparent"
                            color="var(--black)"
                            onClick={(e) => {
                              e.stopPropagation();
                              onDismiss(todo);
                            }}
                            loading={dismissingId === todo.jobRecommendationId}
                            aria-label="Dismiss job"
                          >
                            <BiCheckDouble size={20} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </>
                  ) : (
                    <Text size="sm" c="dimmed">
                      {todo.createdAt
                        ? formatLocalDate(
                            todo.createdAt,
                            DATE_FORMATS.SHORT_DATE_YEAR_FORMAT
                          )
                        : '-'}
                    </Text>
                  )}
                </Group>
              </Table.Td>
            </Table.Tr>
          );
        })}
      </Table.Tbody>
    </Table>
  );
}
